import type { SupabaseClient } from '@supabase/supabase-js';
import { createGrid, type ColDef, type GridApi, type GridOptions, type ICellRendererParams } from 'ag-grid-community';
import { showToast } from '../../lib/toast.js';
import { openErrorModal } from '@vario-connect/frontend-design';
import { awaitJob } from '../../lib/await-job.js';
import { summarizeBulkOutcome, type BulkJobOutcome } from '../../lib/bulk-failure-text.js';
import { loadAllRows } from '../../lib/load-all-rows.js';
import { varioArticleUrl } from '../../lib/vario-links.js';

interface ListToGambioCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

// Direkt aus gambio.vario_listings gelesen (RLS-tenant-scoped, gleiches Direct-
// Read-Pattern wie products.ts auf gambio.items).
interface VarioListingRow {
  vario_article_id: string;
  storefront: string;
  vario_article_number: string | null;
  name: string | null;
  is_variant_main: boolean | null;
  /** Fehlende Pflichtangaben aus der Bereitschaftspruefung. `null` = nie geprueft. */
  missing: string[] | null;
  product_type: string | null;
  checked_at: string | null;
}

interface GridRow {
  varioArticleId: string;
  storefront: string;
  varioArticleNumber: string | null;
  name: string | null;
  isVariantMain: boolean;
  /** `null` = noch nicht geprueft (Zeile aus einem Lauf vor der Pruefung oder Vario nicht lesbar). */
  missing: string[] | null;
}

/**
 * Klartext je Pruefschluessel. Bewusst hier und nicht in der Datenbank: die Formulierung soll sich
 * ohne Migration aendern lassen. Unbekannte Schluessel werden unveraendert durchgereicht, damit
 * eine neue Regel im Worker nicht still verschwindet.
 */
const MANGEL_TEXT: Record<string, string> = {
  category: 'keine Kategorie',
  name: 'keine Bezeichnung',
  price: 'kein Preis',
  tax_schema: 'Steuerschema nicht zugeordnet',
  description: 'keine Beschreibung',
  manufacturer: 'kein Hersteller',
  vpe: 'keine VPE (Grundpreis)',
  weight: 'kein Gewicht',
};

/** Anzeigetext der Spalte „Bereit". Auch der Quick-Filter sucht darin. */
export function bereitschaftsText(missing: string[] | null): string {
  if (missing === null) return 'nicht geprüft';
  if (missing.length === 0) return 'bereit';
  return missing.map((m) => MANGEL_TEXT[m] ?? m).join(', ');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export async function renderListToGambioTab(host: HTMLElement, ctx: ListToGambioCtx): Promise<void> {
  host.innerHTML = `
    <div class="v-card">
      <div class="v-content v-no-padding">
        <p class="text-grey" style="margin:0;padding:12px 16px 0;font-size:12.5px;line-height:1.45;">
          Gezeigt werden VARIO-Artikel, die im Verkaufskanal „Gambio Shop" gelistet und noch nicht im Shop
          angelegt sind. Die Spalte „Bereit" nennt Pflichtangaben, die in VARIO fehlen. Anlegen lässt sich
          ein Artikel trotzdem.
        </p>
        <div class="kl-toolbar" id="gambio-list-toolbar" style="display:flex;gap:0.5em;align-items:center;padding:12px 16px;flex-wrap:wrap;">
          <button type="button" class="v-button" id="gambio-list-refresh" title="Anlegbare Vario-Artikel neu ermitteln">
            <span class="v-icon fa-regular fa-rotate-right" id="gambio-list-refresh-icon"></span> Vario-Artikel aktualisieren
          </button>
          <button type="button" class="v-button v-button-primary" id="gambio-list-create" hidden style="display:none;">
            <span class="v-icon fa-regular fa-cloud-arrow-up"></span> <span id="gambio-list-create-label"></span>
          </button>
          <div class="v-input" style="flex:1 1 200px;max-width:300px;min-width:180px;">
            <span class="v-icon fa-regular fa-magnifying-glass"></span>
            <input type="text" id="gambio-list-search" placeholder="Suche: Artikelnummer oder Name" autocomplete="off"
                   style="border:none;outline:none;background:none;width:100%;font:inherit;color:inherit;" />
          </div>
          <div class="v-select" style="flex:0 0 200px;width:200px;">
            <select id="gambio-list-filter" title="Liste nach Bereitschaft einschränken">
              <option value="alle">Alle anzeigen</option>
              <option value="unvollstaendig">Nur unvollständige</option>
              <option value="bereit">Nur bereite</option>
            </select>
          </div>
          <span class="text-grey" id="gambio-list-count" style="margin-left:auto;"></span>
        </div>
        <div id="gambio-list-grid" class="ag-theme-quartz" style="width:100%;height:560px;"></div>
      </div>
    </div>
  `;
  const gridHost = host.querySelector<HTMLElement>('#gambio-list-grid')!;
  const refreshBtn = host.querySelector<HTMLButtonElement>('#gambio-list-refresh')!;
  const refreshIcon = host.querySelector<HTMLElement>('#gambio-list-refresh-icon')!;
  const createBtn = host.querySelector<HTMLButtonElement>('#gambio-list-create')!;
  const createLabel = host.querySelector<HTMLElement>('#gambio-list-create-label')!;
  const countEl = host.querySelector<HTMLElement>('#gambio-list-count')!;
  const searchEl = host.querySelector<HTMLInputElement>('#gambio-list-search')!;
  const filterEl = host.querySelector<HTMLSelectElement>('#gambio-list-filter')!;
  /** Bereitschafts-Filter. 'alle' = kein externer Filter aktiv. */
  let filterModus: 'alle' | 'unvollstaendig' | 'bereit' = 'alle';
  /** `true`, wenn der Seitenlader vorzeitig abgebrochen hat — dann ist die Liste unvollstaendig. */
  let unvollstaendigGeladen = false;

  // Beide Textspalten verlinken auf die Vario-Artikel-Detailseite (gleiche Geste wie in der
  // Produktuebersicht, Klasse `skulink`). Ohne den Link steht hier nur eine Artikelnummer, und bei
  // einem Artikel ohne Bezeichnung laesst sich nicht einmal nachsehen, worum es geht (Betreiber,
  // 25.09.2026: ein Artikel mit der Nummer „l" ohne Namen). `varioArticleUrl` liefert null, wenn
  // der Vario-Host nicht ermittelbar ist (Standalone) — dann bleibt es bei reinem Text.
  const cols: ColDef<GridRow>[] = [
    {
      headerName: 'SKU',
      field: 'varioArticleNumber',
      width: 220,
      cellStyle: { fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' },
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        const d = p.data;
        if (!d) return '';
        const label = escapeHtml(d.varioArticleNumber ?? '—');
        const url = varioArticleUrl(d.varioArticleId);
        return url
          ? `<a class="skulink" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Artikel in VARIO öffnen">${label}</a>`
          : label;
      },
    },
    {
      headerName: 'Name',
      field: 'name',
      flex: 2,
      minWidth: 240,
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        const d = p.data;
        if (!d) return '';
        const url = varioArticleUrl(d.varioArticleId);
        // Artikel ohne Bezeichnung gibt es wirklich. Eine leere Zelle sagt nichts, deshalb steht
        // dort der Grund im Klartext - ebenfalls verlinkt, damit man nachsehen kann.
        const label = d.name
          ? escapeHtml(d.name)
          : '<span style="color:var(--muted, #737373);">ohne Bezeichnung in VARIO</span>';
        return url
          ? `<a class="skulink" href="${escapeHtml(url)}" target="_blank" rel="noopener" title="Artikel in VARIO öffnen">${label}</a>`
          : label;
      },
    },
    {
      headerName: 'Bereit',
      colId: 'bereit',
      flex: 2,
      minWidth: 230,
      // valueGetter statt field: so findet der Quick-Filter den Klartext und die Spalte laesst
      // sich sortieren (bereite Artikel gruppieren sich dann).
      valueGetter: (p) => bereitschaftsText(p.data?.missing ?? null),
      cellRenderer: (p: ICellRendererParams<GridRow>) => {
        const missing = p.data?.missing ?? null;
        const text = escapeHtml(bereitschaftsText(missing));
        if (missing === null) return `<span style="color:var(--muted, #737373);">${text}</span>`;
        if (missing.length === 0) return `<span style="color:var(--vario-green-dark, #4d7a1f);">${text}</span>`;
        return `<span style="color:#9A6A12;" title="Diese Angaben fehlen in VARIO. Der Artikel lässt sich trotzdem anlegen.">${text}</span>`;
      },
    },
    { headerName: 'Variantenhauptartikel', field: 'isVariantMain', width: 180, valueFormatter: (p) => ((p.value as boolean) ? 'ja' : '—') },
  ];

  const options: GridOptions<GridRow> = {
    columnDefs: cols,
    rowData: [],
    defaultColDef: { sortable: true, resizable: true },
    // Neue v32-Selection-API (wie orders.ts): Checkboxen über rowSelection statt
    // der in v32.2 deprecateten ColDef-Props checkboxSelection/headerCheckboxSelection.
    // selectAll: 'filtered' statt des Standards 'all' - die Kopfzeilen-Auswahl soll die gesetzten
    // Filter beruecksichtigen (Betreiber 27.08.2026). Ohne die Option waehlt der Haken auch
    // weggefilterte Zeilen aus, und das sieht man nicht.
    rowSelection: { mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: true, selectAll: 'filtered' },
    overlayNoRowsTemplate: 'Keine anlegbaren Vario-Artikel. Klick „Vario-Artikel aktualisieren".',
    onSelectionChanged: (e) => updateActionBtns(e.api),
    // Bereitschafts-Filter als EXTERNER Filter (nicht per rowData-Austausch): so bleibt die
    // Auswahl erhalten, und `selectAll: 'filtered'` greift auch auf ihn.
    isExternalFilterPresent: () => filterModus !== 'alle',
    doesExternalFilterPass: (node) => {
      const missing = node.data?.missing ?? null;
      // Ungeprüfte Zeilen zaehlen zu „unvollstaendig": sie sind gerade NICHT als bereit belegt.
      if (filterModus === 'bereit') return missing !== null && missing.length === 0;
      if (filterModus === 'unvollstaendig') return missing === null || missing.length > 0;
      return true;
    },
  };
  const api: GridApi<GridRow> = createGrid(gridHost, options);

  searchEl.addEventListener('input', () => {
    api.setGridOption('quickFilterText', searchEl.value.trim());
    zaehlerSchreiben();
  });
  filterEl.addEventListener('change', () => {
    filterModus = (filterEl.value as typeof filterModus) ?? 'alle';
    api.onFilterChanged();
    zaehlerSchreiben();
  });

  function updateActionBtns(a: GridApi<GridRow>): void {
    const n = a.getSelectedNodes().length;
    // Das `hidden`-Attribut allein reicht hier NICHT: `.v-button { display: inline-flex }` aus
    // bridge.css steht im Author-Stylesheet und schlaegt damit das `[hidden] { display: none }`
    // des Browsers. Ohne Auswahl stand der Knopf sonst unbeschriftet neben „Vario-Artikel
    // aktualisieren" (Befund 24.09.2026 im Verkaufskanal). Deshalb zusaetzlich `display` setzen.
    if (n > 0) {
      createBtn.hidden = false;
      createBtn.style.display = '';
      createLabel.textContent = `${n} auf Gambio anlegen / aktualisieren`;
    } else {
      createBtn.hidden = true;
      createBtn.style.display = 'none';
      createLabel.textContent = '';
    }
  }

  /**
   * Zaehler unter Beruecksichtigung von Suche und Filter. Eine nackte Zahl liesse offen, worauf sie
   * sich bezieht, deshalb steht immer „anlegbare Artikel" dabei — und sobald gefiltert wird, auch
   * die Gesamtzahl.
   */
  function zaehlerSchreiben(): void {
    let alle = 0;
    let bereit = 0;
    api.forEachNode((n) => {
      alle += 1;
      const m = n.data?.missing ?? null;
      if (m !== null && m.length === 0) bereit += 1;
    });
    const sichtbar = api.getDisplayedRowCount();
    const teile: string[] = [];
    teile.push(sichtbar === alle ? `${alle} anlegbare Artikel` : `${sichtbar} von ${alle} anlegbaren Artikeln`);
    teile.push(`${bereit} davon bereit`);
    if (unvollstaendigGeladen) teile.push('Liste unvollständig geladen');
    countEl.textContent = teile.join(' · ');
  }

  async function load(): Promise<void> {
    api.setGridOption('loading', true);
    refreshIcon.classList.add('fa-spin');
    // Seitenweise bis zum Ende statt festem Limit: PostgREST kappt still bei `max_rows` (1000),
    // und eine feste Obergrenze waere fachlich falsch — es muessen alle Artikel erreichbar sein.
    const res = await loadAllRows<VarioListingRow>((from, to) => ctx.supabase.schema('gambio').from('vario_listings')
      .select('vario_article_id, storefront, vario_article_number, name, is_variant_main, missing, product_type, checked_at')
      .order('vario_article_number', { ascending: true, nullsFirst: false }).range(from, to));
    api.setGridOption('loading', false);
    refreshIcon.classList.remove('fa-spin');
    if (res.error) { showToast(`Vario-Artikel nicht ladbar: ${res.error.message}`, 'error'); return; }
    const rows: GridRow[] = ((res.data ?? []) as VarioListingRow[]).map((it) => ({
      varioArticleId: it.vario_article_id,
      storefront: it.storefront,
      varioArticleNumber: it.vario_article_number,
      name: it.name,
      isVariantMain: !!it.is_variant_main,
      // Ohne Zeitstempel gilt die Zeile als ungeprueft, auch wenn `missing` leer aussieht: eine
      // leere Liste aus einem Lauf VOR der Pruefung waere sonst ein falsches „bereit".
      missing: it.checked_at ? (it.missing ?? []) : null,
    }));
    api.setGridOption('rowData', rows);
    // Kein „max. angezeigt" mehr — geladen wird bis zum Ende. Nur wenn die Notbremse des
    // Seitenladers gegriffen hat, ist die Liste unvollstaendig, und das wird benannt.
    unvollstaendigGeladen = res.truncated;
    zaehlerSchreiben();
  }

  async function refresh(): Promise<void> {
    refreshBtn.disabled = true;
    refreshIcon.classList.add('fa-spin');
    try {
      // Refresh pro Shop anstoßen. gambio_list_shops liefert die shop_keys.
      const shopsRes = await ctx.supabase.rpc('gambio_list_shops');
      const shops = (Array.isArray(shopsRes.data) ? shopsRes.data : []) as Array<{ shop_key: string }>;
      const jobIds: string[] = [];
      for (const s of shops) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_refresh_vario_listings', { _shop_key: s.shop_key });
        if (error) throw new Error(error.message);
        const rec = Array.isArray(data) ? data[0] : data;
        if (rec?.job_id) jobIds.push(rec.job_id as string);
      }
      showToast('Vario-Artikel-Ermittlung läuft …', 'info');
      const outcomes: BulkJobOutcome[] = [];
      for (const id of jobIds) {
        const job = await awaitJob(ctx.supabase, id, 180_000);
        outcomes.push({ status: job.status, error: job.error, result: job.result });
      }
      await load();
      // Meldung nennt die Ursache aus dem Job, statt auf die Worker-Logs zu verweisen (13.3).
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Vario-Artikel-Ermittlung',
        successMessage: 'Vario-Artikel aktualisiert.',
      });
      if (summary.hasFailures) {
        openErrorModal({ title: 'Ermittlung fehlgeschlagen', items: summary.items, context: { action: 'gambio_refresh_vario_listings' }, json: { outcomes } });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      showToast(`Aktualisieren fehlgeschlagen: ${(e as Error).message}`, 'error');
    } finally {
      refreshBtn.disabled = false;
      refreshIcon.classList.remove('fa-spin');
    }
  }

  async function createSelected(): Promise<void> {
    const byShop = new Map<string, string[]>();
    for (const n of api.getSelectedNodes()) {
      if (!n.data) continue;
      const arr = byShop.get(n.data.storefront) ?? [];
      arr.push(n.data.varioArticleId);
      byShop.set(n.data.storefront, arr);
    }
    if (byShop.size === 0) return;
    createBtn.disabled = true;
    showToast('Artikel werden auf Gambio angelegt / aktualisiert …', 'info');
    try {
      const jobIds: string[] = [];
      for (const [shopKey, ids] of byShop) {
        const { data, error } = await ctx.supabase.rpc('gambio_enqueue_sync_to_gambio', { _shop_key: shopKey, _vario_article_ids: ids });
        if (error) throw new Error(error.message);
        for (const rec of (Array.isArray(data) ? data : [data])) if (rec?.job_id) jobIds.push(rec.job_id as string);
      }
      if (jobIds.length === 0) { showToast('Kein Anlegen / Aktualisieren möglich - Auswahl ggf. veraltet, bitte aktualisieren.', 'info'); await load(); return; }
      // Artikelnummern der AUSGEWÄHLTEN Zeilen merken, bevor `load()` das Grid neu befüllt: der
      // Worker meldet nur Vario-Artikel-IDs, die Nummer kennt hier nur das Grid.
      const numberByArticleId = new Map<string, string>();
      for (const n of api.getSelectedNodes()) {
        if (n.data?.varioArticleNumber) numberByArticleId.set(n.data.varioArticleId, n.data.varioArticleNumber);
      }
      const outcomes: BulkJobOutcome[] = [];
      for (const id of jobIds) {
        const job = await awaitJob(ctx.supabase, id, 180_000);
        outcomes.push({ status: job.status, error: job.error, result: job.result });
      }
      await load();
      // Nennt Artikel UND Ursache; erkennt außerdem Teil-Fehlschläge in einem 'completed'-Job,
      // die vorher als voller Erfolg gemeldet wurden (13.3).
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Anlegen / Aktualisieren',
        successMessage: 'Artikel auf Gambio angelegt / aktualisiert.',
        articleLabel: (articleId) => numberByArticleId.get(articleId) ?? null,
      });
      if (summary.hasFailures) {
        // Modal statt Toast: bei mehreren Artikeln wurde der Einzeiler unlesbar.
        openErrorModal({
          title: 'Anlegen / Aktualisieren teilweise fehlgeschlagen',
          intro: 'Die Ursache steht auch am jeweiligen Artikel unter „Letzter Fehler".',
          items: summary.items,
          context: { action: 'gambio_sync_to_gambio' },
          json: { outcomes },
        });
      } else {
        showToast(summary.message, 'success');
      }
    } catch (e) {
      showToast(`Anlegen / Aktualisieren fehlgeschlagen: ${(e as Error).message}`, 'error');
    } finally {
      createBtn.disabled = false;
    }
  }

  refreshBtn.addEventListener('click', () => void refresh());
  createBtn.addEventListener('click', () => void createSelected());
  await load();
}
